import { useCallback, useRef, useState } from 'react';

const HISTORY_LIMIT = 100;

// Holds the canvas graph ({ blocks, connections }) together with a client-side
// undo/redo history of immutable snapshots and an unsaved-changes flag.
export default function useFlowGraph() {
  const [graph, setGraphState] = useState({ blocks: [], connections: [] });
  const graphRef = useRef(graph);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [dirty, setDirty] = useState(false);
  // Bumped whenever the history stacks change so canUndo/canRedo re-render.
  const [, setHistoryVersion] = useState(0);

  const bumpHistory = useCallback(() => setHistoryVersion(version => version + 1), []);

  const pushUndoSnapshot = useCallback(() => {
    undoStack.current.push(graphRef.current);
    if (undoStack.current.length > HISTORY_LIMIT) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  // Applies a graph change. `record: false` skips the history snapshot (used for
  // continuous drag updates after beginInteraction already recorded one).
  const setGraph = useCallback(
    (next, { record = true, markDirty = true } = {}) => {
      const value = typeof next === 'function' ? next(graphRef.current) : next;
      if (record) pushUndoSnapshot();
      graphRef.current = value;
      setGraphState(value);
      if (markDirty) setDirty(true);
      bumpHistory();
    },
    [bumpHistory, pushUndoSnapshot]
  );

  // Records exactly one history snapshot at the start of a continuous interaction.
  const beginInteraction = useCallback(() => {
    pushUndoSnapshot();
    bumpHistory();
  }, [bumpHistory, pushUndoSnapshot]);

  // Replaces the graph without history / dirty flag (initial load).
  const resetGraph = useCallback(
    value => {
      undoStack.current = [];
      redoStack.current = [];
      graphRef.current = value;
      setGraphState(value);
      setDirty(false);
      bumpHistory();
    },
    [bumpHistory]
  );

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const previous = undoStack.current.pop();
    redoStack.current.push(graphRef.current);
    graphRef.current = previous;
    setGraphState(previous);
    setDirty(true);
    bumpHistory();
  }, [bumpHistory]);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current.pop();
    undoStack.current.push(graphRef.current);
    graphRef.current = next;
    setGraphState(next);
    setDirty(true);
    bumpHistory();
  }, [bumpHistory]);

  return {
    blocks: graph.blocks,
    connections: graph.connections,
    graphRef,
    setGraph,
    resetGraph,
    beginInteraction,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    dirty,
    setDirty,
  };
}
